/* assets/intaprint-custom.js
 * Intaprint custom overrides (Horizon-safe)
 */
// A separate, successfully uploaded QR-code file can provide the variable second side.
// Recognise the stored IPO option value (Add individual QR codes), not just its Yes display label.
function intaprintQrProvidesSecondSide() {
  const fields = Array.from(document.querySelectorAll('.intaprint-product-options .ipo-field'));
  const qrSelection = fields.find(field =>
    /would you like individual qr codes printed/i.test(field.querySelector('.ipo-label')?.textContent || '')
  );
  const qrSelected = qrSelection?.querySelector('select')?.value || '';
  if (!/^(?:yes\b|add individual qr codes\b)/i.test(qrSelected.trim())) return false;

  const qrField = fields.find(field =>
    /individual qr-code files/i.test(field.querySelector('.ipo-label')?.textContent || '')
  );
  if (!qrField || !(qrField.offsetWidth || qrField.offsetHeight || qrField.getClientRects().length)) return false;

  const card = qrField.querySelector('.ipo-upload-card');
  return !!(card && !card.hidden &&
    !card.querySelector('.ipo-upload-tick--uploading') &&
    /Artwork Uploaded Successfully/i.test(card.querySelector('.ipo-upload-title')?.textContent || '') &&
    card.querySelectorAll('.ipo-upload-preview-item').length > 0);
}

(() => {
  'use strict';

  const Intaprint = {
    init() {
      if (window.__INTAPRINT_INIT__) return;
      window.__INTAPRINT_INIT__ = true;

      this.bindQtyRules();
      this.bindStickyATC();
      this.bindHideGoogleVariants(); // NEW
    },

    /* ----------------------------
      Hide "Google – From Price" option in UI (keep variant for Merchant Center)
      Robust version for Horizon-style custom pickers.
    ---------------------------- */
    bindHideGoogleVariants() {
      const isGoogle = (txt) => (txt || '').toString().toLowerCase().includes('google');

      const getRoots = (scope = document) => {
        const roots = scope.querySelectorAll('.product-information');
        return roots.length ? Array.from(roots) : [document];
      };

      const hideSelectOptions = (root) => {
        root.querySelectorAll('select[name^="options"], select[data-option]').forEach((select) => {
          let changed = false;

          Array.from(select.options || []).forEach((opt) => {
            if (isGoogle(opt.textContent || opt.label || '')) {
              opt.hidden = true;
              opt.disabled = true;
              if (opt.selected) changed = true;
            }
          });

          if (changed) {
            const firstOk = Array.from(select.options || []).find((o) => !o.disabled && !o.hidden);
            if (firstOk) {
              select.value = firstOk.value;
              select.dispatchEvent(new Event('change', { bubbles: true }));
              select.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
        });
      };

      const hideRadioLabels = (root) => {
        const radios = root.querySelectorAll('input[type="radio"][name^="options"]');
        radios.forEach((radio) => {
          const label =
            root.querySelector(`label[for="${CSS.escape(radio.id)}"]`) ||
            radio.closest('label');

          const text = (label ? label.textContent : '').trim();
          if (isGoogle(text)) {
            if (label) label.style.display = 'none';
            radio.disabled = true;

            if (radio.checked) {
              radio.checked = false;
              const group = root.querySelectorAll(`input[type="radio"][name="${CSS.escape(radio.name)}"]`);
              const firstOk = Array.from(group).find((r) => !r.disabled);
              if (firstOk) {
                firstOk.checked = true;
                firstOk.dispatchEvent(new Event('change', { bubbles: true }));
                firstOk.dispatchEvent(new Event('input', { bubbles: true }));
              }
            }
          }
        });
      };

      // Horizon/custom pickers (buttons/chips/swatches)
      const hideCustomPickerOptions = (root) => {
        // Only look inside common option containers so we don't hide random buttons
        const containers = root.querySelectorAll(
          'variant-selects, variant-picker, .variant-picker, .product-form__input, .product-form__inputs, .product__info, [data-variant-picker], [data-variant-selects]'
        );

        const scopes = containers.length ? Array.from(containers) : [root];

        // 1) Prefer elements that look like option values via data attributes
        scopes.forEach((scope) => {
          const dataCandidates = scope.querySelectorAll(
            '[data-value],[data-option-value],[data-option],[data-variant-option],[data-variant-title],[role="radio"]'
          );

          dataCandidates.forEach((el) => {
            // Get the "label" for this option
            const label = (
              el.getAttribute('data-value') ||
              el.getAttribute('data-option-value') ||
              el.getAttribute('data-option') ||
              el.getAttribute('data-variant-option') ||
              el.getAttribute('data-variant-title') ||
              el.textContent ||
              ''
            ).trim();

            // Only hide the element itself (not parents/wrappers)
            if (label && isGoogle(label)) {
              el.style.display = 'none';

              // If it’s an ARIA radio, also mark disabled
              if (el.getAttribute('role') === 'radio') {
                el.setAttribute('aria-disabled', 'true');
                el.setAttribute('tabindex', '-1');
              }

              // If it's a button-like option, disable it too
              if (el.tagName === 'BUTTON') el.disabled = true;
            }
          });

          // 2) Fallback: hide the smallest clickable element whose text is exactly the Google label
          // This avoids nuking wrappers.
          const exactText = 'google – from price';
          const clickable = scope.querySelectorAll('button, a, [role="button"], [role="radio"], label, .swatch, .chip, .pill');
          clickable.forEach((el) => {
            const txt = (el.textContent || '').trim().toLowerCase();
            if (txt === exactText || (txt && isGoogle(txt) && txt.includes('from price'))) {
              el.style.display = 'none';
              if (el.tagName === 'BUTTON') el.disabled = true;
              if (el.getAttribute('role') === 'radio') {
                el.setAttribute('aria-disabled', 'true');
                el.setAttribute('tabindex', '-1');
              }
            }
          });
        });
      };

      const forceAwayIfGoogleSelected = (root) => {
        // If the current selected variant title includes "Google", force to first visible option.
        // This is defensive—ideally user can’t select it once hidden.
        const idInput = root.querySelector('form[action^="/cart/add"] input[name="id"]');
        if (!idInput) return;

        // If theme exposes current selected option labels, try to detect it
        const selectedVisible = root.querySelector('[aria-checked="true"], .is-selected, .selected, .active');
        const selectedText = (selectedVisible?.textContent || '').trim();
        if (isGoogle(selectedText)) {
          const firstOk = root.querySelector(
            'button:not([disabled]):not([style*="display: none"]), [role="radio"][aria-disabled!="true"]:not([style*="display: none"])'
          );
          if (firstOk) firstOk.click();
        }
      };

      const apply = (scope = document) => {
        getRoots(scope).forEach((root) => {
          hideSelectOptions(root);
          hideRadioLabels(root);
          hideCustomPickerOptions(root);
          forceAwayIfGoogleSelected(root);

          // NEW: reveal pickers after cleanup (prevents flash)
          root.setAttribute('data-intaprint-variant-clean', 'true');
        });
      };

      const boot = () => {
        apply(document);

        // Re-apply after dynamic updates / theme editor
        document.addEventListener('shopify:section:load', (evt) => apply(evt.target));

        // Your theme emits variant:changed; re-apply because options can re-render
        document.addEventListener('variant:changed', () => apply(document));

        // Some Horizon interactions update DOM without variant:changed
        const obs = new MutationObserver(() => apply(document));
        obs.observe(document.documentElement, { childList: true, subtree: true });
        // NOTE: MutationObserver is broad; but the work we do is cheap and only hides matching "Google" labels.
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot, { once: true });
      } else {
        boot();
      }
    },

    /* ----------------------------
       Quantity rules (moved from theme.liquid)
    ---------------------------- */
    bindQtyRules() {
      const getRules = () => {
        const raw = window.IntaprintQtyRules || {};
        const minRaw = Number(raw.minQty);
        const stepRaw = Number(raw.step);
        const baseMaxRaw = Number(raw.maxQty);
        const expressMaxRaw = Number(window.IntaprintExpressProductionMaxQty);

        const min = Number.isFinite(minRaw) && minRaw > 1 ? minRaw : 1;
        const hasStep = Number.isFinite(stepRaw) && stepRaw > 1;
        const step = hasStep ? stepRaw : 1;

        // Express Production can temporarily override the normal variant maximum.
        // If Express is not selected, window.IntaprintExpressProductionMaxQty is null/blank.
        const maxRaw = Number.isFinite(expressMaxRaw) && expressMaxRaw >= min
          ? expressMaxRaw
          : baseMaxRaw;

        const hasMax = Number.isFinite(maxRaw) && maxRaw >= min;
        const max = hasMax ? Math.floor(maxRaw) : null;

        return { min, step, hasStep, max, hasMax };
      };

      const clampToRules = (value, rules) => {
        let v = Number(value);
        if (!Number.isFinite(v) || v < rules.min) v = rules.min;
        if (rules.hasMax && v > rules.max) v = rules.max;
        return v;
      };

      const getLargestSteppedQtyAtOrBelowMax = (rules) => {
        if (!rules.hasMax) return null;
        if (!rules.hasStep) return rules.max;

        const steps = Math.floor((rules.max - rules.min) / rules.step);
        return rules.min + Math.max(0, steps) * rules.step;
      };

      const snapNearest = (value, rules) => {
        let v = Number(value);
        if (!Number.isFinite(v) || v < rules.min) return rules.min;

        if (rules.hasMax && v > rules.max) {
          return getLargestSteppedQtyAtOrBelowMax(rules);
        }

        const offset = v - rules.min;
        const stepIndex = Math.round(offset / rules.step);
        let snapped = rules.min + stepIndex * rules.step;

        if (snapped < rules.min) snapped = rules.min;

        if (rules.hasMax && snapped > rules.max) {
          snapped = getLargestSteppedQtyAtOrBelowMax(rules);
        }

        return snapped;
      };

      const normaliseQuantityForRules = (value, rules) => {
        if (rules.hasStep) return snapNearest(value, rules);
        return clampToRules(value, rules);
      };

      const updateQuantityButtonStates = (selector, qty, rules = null) => {
        const minusBtn = selector?.querySelector('.quantity-minus');
        const plusBtn = selector?.querySelector('.quantity-plus');
        const r = rules || { min: 1, hasMax: false, max: null };

        if (minusBtn) {
          const shouldDisableMinus = qty <= r.min;
          minusBtn.toggleAttribute('disabled', shouldDisableMinus);
          minusBtn.setAttribute('aria-disabled', shouldDisableMinus ? 'true' : 'false');
        }

        if (plusBtn) {
          const shouldDisablePlus = !!(r.hasMax && qty >= r.max);
          plusBtn.toggleAttribute('disabled', shouldDisablePlus);
          plusBtn.setAttribute('aria-disabled', shouldDisablePlus ? 'true' : 'false');
        }
      };

      const findMainSelector = (root) =>
        root.querySelector('.product-information quantity-selector-component');

      const normaliseText = (value) =>
        String(value || '').trim().toLowerCase();

      const getFieldLabelText = (field) => {
        const parts = [];

        if (field.id) {
          const label = document.querySelector(`label[for="${CSS.escape(field.id)}"]`);
          if (label) parts.push(label.textContent || '');
        }

        const previousLabel = field.previousElementSibling;
        if (previousLabel && previousLabel.matches('label, .ipo-label')) {
          parts.push(previousLabel.textContent || '');
        }

        const wrappingLabel = field.closest('label');
        if (wrappingLabel) parts.push(wrappingLabel.textContent || '');

        const fieldWrap = field.closest('.ipo-field, [data-option], .option, .product-option, .product-form__input, fieldset');
        const nearbyLabel = fieldWrap?.querySelector('label, legend, .ipo-label, .label, .form__label');
        if (nearbyLabel) parts.push(nearbyLabel.textContent || '');

        return parts.join(' ');
      };

      const isSampleOrderField = (field) => {
        if (!(field instanceof HTMLSelectElement || field instanceof HTMLInputElement)) return false;

        const labelText = normaliseText([
          field.getAttribute('name'),
          field.getAttribute('id'),
          field.getAttribute('data-name'),
          field.getAttribute('data-key'),
          field.getAttribute('data-option-name'),
          field.getAttribute('aria-label'),
          getFieldLabelText(field)
        ].join(' '));

        if (/sample_order/.test(labelText)) return true;
        if (!/sample/.test(labelText)) return false;

        if (field instanceof HTMLSelectElement) {
          const optionText = Array.from(field.options || [])
            .map((option) => `${option.value || ''} ${option.textContent || ''}`)
            .join(' ');
          return /order a sample|sample/.test(normaliseText(optionText));
        }

        return /order a sample|sample/.test(labelText);
      };

      const isSampleOrderSelected = () => {
        const fields = Array.from(document.querySelectorAll('.intaprint-product-options select, .intaprint-product-options input, .ipo-fields select, .ipo-fields input'));

        return fields.some((field) => {
          if (!isSampleOrderField(field)) return false;

          if (field instanceof HTMLSelectElement) {
            const selectedOption = field.options[field.selectedIndex];
            const selectedText = normaliseText([
              field.value,
              selectedOption?.textContent
            ].join(' '));

            return selectedText === 'yes' ||
              /yes.*order(?: a)?(?: blank| printed)? sample/.test(selectedText) ||
              /order(?: a)?(?: blank| printed)? sample/.test(selectedText);
          }

          if (field instanceof HTMLInputElement) {
            const type = normaliseText(field.type);
            if ((type === 'checkbox' || type === 'radio') && !field.checked) return false;

            const selectedText = normaliseText([
              field.value,
              field.getAttribute('aria-label'),
              getFieldLabelText(field)
            ].join(' '));

            return selectedText === 'yes' ||
              /yes.*order(?: a)?(?: blank| printed)? sample/.test(selectedText) ||
              /order(?: a)?(?: blank| printed)? sample/.test(selectedText);
          }

          return false;
        });
      };

      const setQuantityValue = (selector, value, rules = null) => {
        if (!selector) return;
        const input = selector.querySelector('input[name="quantity"], input[name="updates[]"]');
        if (!input) return;

        const r = rules || { min: 1, hasMax: false, max: null };
        const qty = rules ? normaliseQuantityForRules(value, r) : Math.max(1, Number(value) || 1);

        input.value = qty;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));

        updateQuantityButtonStates(selector, qty, r);
      };

      const applyRulesToSelector = (selector, opts = {}) => {
        if (!selector) return;

        const input = selector.querySelector('input[name="quantity"], input[name="updates[]"]');
        if (!input) return;

        // Avoid native step weirdness
        input.setAttribute('step', '1');
        input.step = 1;

        const rules = getRules();

        if (rules.hasMax) {
          input.setAttribute('max', String(rules.max));
        } else {
          input.removeAttribute('max');
        }

        // Helper element positioned by CSS, we just control content/display
        let helper = selector.querySelector('.intaprint-qty-helper');
        if (!helper) {
          helper = document.createElement('div');
          helper.className = 'intaprint-qty-helper';
          selector.appendChild(helper);
        }

        const isSampleOrder = isSampleOrderSelected();

        if (isSampleOrder) {
          helper.innerHTML = '';
          helper.style.display = 'none';
        } else {
          const helperParts = [];

          if (rules.min > 1) {
            helperParts.push('Minimum: <strong>' + rules.min.toLocaleString() + '</strong>');
          }

          if (rules.hasStep) {
            helperParts.push('Multiples of: <strong>' + rules.step.toLocaleString() + '</strong>');
          }

          if (rules.hasMax) {
            helperParts.push('Maximum: <strong>' + rules.max.toLocaleString() + '</strong>');
          }

          helper.innerHTML = helperParts.join(' • ');
          helper.style.display = helperParts.length ? 'block' : 'none';
        }

        // Sample orders bypass MOQ/increment rules and use normal 1-by-1 quantities.
        if (isSampleOrder) {
          let sampleVal = Number(input.value);

          if (opts.forceToSampleOne === true || !Number.isFinite(sampleVal) || sampleVal < 1) {
            sampleVal = 1;
          }

          setQuantityValue(selector, sampleVal);
          return;
        }

        // Normalise current value to rules
        let val = Number(input.value);
        if (!Number.isFinite(val)) val = rules.min;

        // On variant change, always reset to the new min
        if (opts.forceToMin === true) {
          val = rules.min;
        } else {
          val = normaliseQuantityForRules(val, rules);
        }

        setQuantityValue(selector, val, rules);
      };

      const bindSelector = (root) => {
        const selector = findMainSelector(root);
        if (!selector) return;

        // If already bound, DO NOT skip — just re-apply rules (without forcing reset)
        if (selector.dataset.intaprintBound === 'true') {
          applyRulesToSelector(selector, { forceToMin: false });
          return;
        }

        selector.dataset.intaprintBound = 'true';

        const input = selector.querySelector('input[name="quantity"], input[name="updates[]"]');
        if (!input) return;

        // Manual typing → enforce min and step (if any)
        input.addEventListener('blur', function () {
          if (isSampleOrderSelected()) {
            let val = Number(input.value);
            if (!Number.isFinite(val) || val < 1) val = 1;
            setQuantityValue(selector, val);
            return;
          }

          const r = getRules();
          let val = Number(input.value);

          if (!Number.isFinite(val)) val = r.min;

          val = normaliseQuantityForRules(val, r);

          setQuantityValue(selector, val, r);
        });

        // Initial apply (no forced reset)
        setTimeout(() => applyRulesToSelector(selector, { forceToMin: false }), 50);
      };

      // Read per-variant rules from JSON map output in product-information.liquid
      const refreshRulesForVariant = (variantId) => {
        const mapEl = document.getElementById('IntaprintQtyRulesMap');
        if (!mapEl) return;

        let map;
        try {
          map = JSON.parse(mapEl.textContent || '{}');
        } catch (e) {
          return;
        }

        const key = variantId != null ? String(variantId).trim() : null;

        const rules =
          (key && map.variants && map.variants[key]) ||
          map.default ||
          { minQty: 1, step: 0, maxQty: null };

        window.IntaprintQtyRules = rules;
      };

      const getSelectedVariantId = () => {
        const idInput = document.querySelector('form[action^="/cart/add"] input[name="id"]');
        return idInput ? idInput.value : null;
      };

      const boot = () => {
        // Ensure rules are set for initially-selected variant
        refreshRulesForVariant(getSelectedVariantId());

        // Initial bind
        bindSelector(document);

        // Re-bind on dynamic section load
        document.addEventListener('shopify:section:load', (event) => bindSelector(event.target));

        // Sample dropdown changes:
        // - Yes = reset to 1 and allow 1-by-1 sample quantities
        // - No / blank = restore the selected variant's MOQ/increment rules
        document.addEventListener('change', (event) => {
          const target = event.target;
          if (!(target instanceof HTMLElement)) return;
          if (!isSampleOrderField(target)) return;

          const selector = document.querySelector('.product-information quantity-selector-component');
          if (!selector) return;

          if (isSampleOrderSelected()) {
            applyRulesToSelector(selector, { forceToSampleOne: true });
          } else {
            applyRulesToSelector(selector, { forceToMin: true });
          }

          if (typeof window.IntaprintDiscountTableRefresh === 'function') {
            window.IntaprintDiscountTableRefresh({ reason: 'sample-order-change' });
          }
        });

        const restoreNormalQtyRulesForVariant = (variantId, reason = 'variant-change') => {
          refreshRulesForVariant(variantId || getSelectedVariantId());

          const selector = document.querySelector('.product-information quantity-selector-component');
          if (!selector) return;

          // Variant changes reset the option set, so never keep the old Sample quantity state.
          // Force the newly-selected variant back to its normal MOQ/increment rules.
          applyRulesToSelector(selector, { forceToMin: true });

          if (typeof window.IntaprintDiscountTableRefresh === 'function') {
            window.IntaprintDiscountTableRefresh({ reason });
          }
        };

        // On variant change: refresh rules AND force quantity back to the new min.
        // Run again shortly afterwards because Horizon/IPO can morph the option markup after the variant event.
        document.addEventListener('variant:changed', (evt) => {
          const variantId = evt?.detail?.variant?.id || getSelectedVariantId();

          restoreNormalQtyRulesForVariant(variantId, 'variant-changed');

          setTimeout(() => restoreNormalQtyRulesForVariant(variantId, 'variant-changed-delayed'), 100);
          setTimeout(() => restoreNormalQtyRulesForVariant(variantId, 'variant-changed-delayed-2'), 300);
        });

        // IPO dispatches this after rendering the newly-selected variant's sample field.
        // Respect the actual sample state supplied by IPO:
        // - sample selected/defaulted = quantity 1, increments of 1
        // - standard quantities = restore the variant MOQ/increment rules
        document.addEventListener('intaprint:sample-order-reset', (evt) => {
          const variantId = evt?.detail?.variantId || getSelectedVariantId();
          const isSample = evt?.detail?.isSample === true;

          refreshRulesForVariant(variantId);

          const applyCurrentSampleState = (reason) => {
            const selector = document.querySelector('.product-information quantity-selector-component');
            if (!selector) return;

            if (isSample) {
              applyRulesToSelector(selector, { forceToSampleOne: true });
            } else {
              applyRulesToSelector(selector, { forceToMin: true });
            }

            if (typeof window.IntaprintDiscountTableRefresh === 'function') {
              window.IntaprintDiscountTableRefresh({ reason });
            }
          };

          setTimeout(() => applyCurrentSampleState('sample-order-reset'), 0);
          setTimeout(() => applyCurrentSampleState('sample-order-reset-delayed'), 100);
          setTimeout(() => applyCurrentSampleState('sample-order-reset-delayed-2'), 300);
        });

        // Express Production can temporarily reduce the maximum quantity.
        // Re-apply the current rules when the customer switches between Standard and Express.
        document.addEventListener('intaprint:express-production-change', () => {
          refreshRulesForVariant(getSelectedVariantId());

          const selector = document.querySelector('.product-information quantity-selector-component');
          if (!selector) return;

          applyRulesToSelector(selector, { forceToMin: false });
        });

        /* ---------------------------------------------------------
           OVERRIDE +/- BUTTONS ONLY WHEN STEP METAFIELD IS SET
           - No step metafield: Horizon handles (1,2,3…)
           - With step: our logic takes over
        --------------------------------------------------------- */
        document.addEventListener(
          'click',
          function (evt) {
            const btn = evt.target.closest('.quantity-plus, .quantity-minus');
            if (!btn) return;

            const selector = btn.closest('quantity-selector-component');
            if (!selector) return;

            const productInfo = selector.closest('.product-information');
            if (!productInfo) return;

            const input = selector.querySelector('input[name="quantity"], input[name="updates[]"]');
            if (!input) return;

            const rules = getRules();
            const sampleMode = isSampleOrderSelected();

            if (sampleMode) {
              // In Sample mode, let Horizon's normal button handler do the click.
              // Then normalise afterwards so samples stay at 1, 2, 3, 4...
              setTimeout(() => {
                let sampleVal = Number(input.value);
                if (!Number.isFinite(sampleVal) || sampleVal < 1) sampleVal = 1;
                setQuantityValue(selector, sampleVal);
              }, 0);
              return;
            }

            const isPlus = btn.classList.contains('quantity-plus');

            // Standard MOQ/max-only products, e.g. minimum 10 / increment 1:
            // Let Horizon handle normal +/- so it does not double-increment.
            // Only block the buttons when they would go outside min/max.
            if (!rules.hasStep) {
              let current = Number(input.value);
              if (!Number.isFinite(current)) current = rules.min;

              if (!isPlus && current <= rules.min) {
                evt.preventDefault();
                evt.stopPropagation();
                evt.stopImmediatePropagation();
                setQuantityValue(selector, rules.min, rules);
                return;
              }

              if (isPlus && rules.hasMax && current >= rules.max) {
                evt.preventDefault();
                evt.stopPropagation();
                evt.stopImmediatePropagation();
                setQuantityValue(selector, rules.max, rules);
                return;
              }

              // Horizon handles the actual +/- click; clamp afterwards if needed.
              setTimeout(() => {
                setQuantityValue(selector, input.value, rules);
              }, 0);

              return;
            }

            // Step mode: take full control and jump exactly by step.
            evt.preventDefault();
            evt.stopPropagation();
            evt.stopImmediatePropagation();

            let current = Number(input.value);

            if (!Number.isFinite(current)) current = rules.min;

            current += isPlus ? rules.step : -rules.step;
            current = snapNearest(current, rules);

            setQuantityValue(selector, current, rules);
          },
          true
        );

        /* ---------------------------------------------------------
          BEFORE ADD TO CART: ENFORCE RULES + REQUIRED ARTWORK UPLOAD
        --------------------------------------------------------- */
        document.addEventListener(
          'click',
          function (evt) {
            const btn = evt.target.closest(
              '[ref="addToCartButton"], button[name="add"], input[name="add"], form[action^="/cart/add"] button[type="submit"], form[action^="/cart/add"] input[type="submit"]'
            );
            if (!btn) return;

            const productInfo = btn.closest('.product-information');
            if (!productInfo) return;

            const form = btn.closest('form[action^="/cart/add"]');

            const selectedText = Array.from(
              productInfo.querySelectorAll('variant-picker input:checked, variant-picker select')
            )
              .map((el) => {
                if (el.tagName === 'SELECT') {
                  return el.options[el.selectedIndex]?.textContent || '';
                }

                const label =
                  productInfo.querySelector(`label[for="${CSS.escape(el.id)}"]`) ||
                  el.closest('label');

                return label?.textContent || '';
              })
              .join(' ');

            const isPrintedProduct = /printed/i.test(selectedText);

            // Horizon can start an AJAX cart request directly from the click.
            // Printed products may also pause and resubmit the form while the
            // Express Production date is checked.  Cancel the original click
            // and release one normal form submission after our synchronous
            // option/quantity checks have finished.
            const submitPrintedFormOnce = () => {
              if (!isPrintedProduct || !form) return false;

              evt.preventDefault();
              evt.stopPropagation();
              evt.stopImmediatePropagation();

              if (form.dataset.intaprintSubmitQueued === '1') return true;
              form.dataset.intaprintSubmitQueued = '1';

              queueMicrotask(() => {
                try {
                  if (typeof form.requestSubmit === 'function') {
                    const canUseSubmitter =
                      (btn instanceof HTMLButtonElement || btn instanceof HTMLInputElement) &&
                      btn.form === form &&
                      String(btn.type || '').toLowerCase() === 'submit';

                    if (canUseSubmitter) {
                      form.requestSubmit(btn);
                    } else {
                      form.requestSubmit();
                    }
                  } else {
                    form.submit();
                  }
                } finally {
                  setTimeout(() => {
                    delete form.dataset.intaprintSubmitQueued;
                  }, 2000);
                }
              });

              return true;
            };

            if (isPrintedProduct && form) {
              const optionFields = Array.from(
                document.querySelectorAll('.intaprint-product-options .ipo-field')
              );

              const isFieldVisible = (field) =>
                !!(field.offsetWidth || field.offsetHeight || field.getClientRects().length);

              const getIpoFieldLabel = (field) => {
                const label = field.querySelector('.ipo-label');
                if (!label) return 'this option';

                return Array.from(label.childNodes)
                  .filter((node) => !(node.nodeType === 1 && node.classList?.contains('ipo-required')))
                  .map((node) => node.textContent || '')
                  .join('')
                  .replace('*', '')
                  .trim() || 'this option';
              };

              const clearInlineIpoErrors = () => {
                document
                  .querySelectorAll('.intaprint-ipo-inline-error, .intaprint-upload-required-error')
                  .forEach((el) => el.remove());
              };

              const showInlineIpoError = (field, message) => {
                const error = document.createElement('div');
                error.className = 'intaprint-ipo-inline-error';

                error.style.padding = '4px 0';
                error.style.marginTop = '4px';
                error.style.marginBottom = '8px';
                error.style.border = '0';
                error.style.background = 'transparent';
                error.style.color = '#c93a3a';
                error.style.fontSize = '14px';
                error.style.fontWeight = '400';
                error.style.width = 'auto';

                error.innerHTML = `⚠ ${message}`;
                field.appendChild(error);

                return error;
              };

              clearInlineIpoErrors();

              let firstError = null;

              optionFields.forEach((field) => {
                if (!isFieldVisible(field)) return;
                if (!field.querySelector('.ipo-required')) return;
                if (field.querySelector('input[type="file"]')) return;

                const label = getIpoFieldLabel(field);
                const select = field.querySelector('select');
                const swatchLabel = field.querySelector('.ipo-sd-label')?.textContent?.trim() || '';
                const hasSwatchDropdown = !!field.querySelector('.ipo-sd');
                const hasVisibleSwatchValue = hasSwatchDropdown && swatchLabel && swatchLabel.toLowerCase() !== 'select a value';
                const textarea = field.querySelector('textarea');
                const textInput = field.querySelector('input:not([type="file"]):not([type="hidden"]):not([type="radio"]):not([type="checkbox"])');
                const choiceInputs = field.querySelectorAll('input[type="radio"], input[type="checkbox"]');
                const checkedChoice = field.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');

                let message = '';

                if (hasSwatchDropdown) {
                  if (!hasVisibleSwatchValue) {
                    message = `Please select a value for: ${label}`;
                  }
                } else if (select && !String(select.value || '').trim()) {
                  message = `Please select a value for: ${label}`;
                } else if (textarea && !String(textarea.value || '').trim()) {
                  message = `${label} is required.`;
                } else if (textInput && !String(textInput.value || '').trim()) {
                  message = `${label} is required.`;
                } else if (choiceInputs.length && !checkedChoice) {
                  message = `Please select a value for: ${label}`;
                }

                if (message) {
                  const err = showInlineIpoError(field, message);
                  if (!firstError) firstError = err;
                }
              });

              const requiredUploadFields = optionFields.filter((field) => {
                const fileInput = field.querySelector('input[type="file"]');
                const isRequired = !!field.querySelector('.ipo-required');

                return fileInput && isFieldVisible(field) && isRequired;
              });

              requiredUploadFields.forEach((uploadField) => {
                const fileInput = uploadField.querySelector('input[type="file"]');
                const uploadedFiles = fileInput?.files ? fileInput.files.length : 0;

                const uploadBtn = uploadField.querySelector('.ipo-file-btn');
                const btnText = uploadBtn?.textContent?.trim() || '';

                let requiredFiles = 1;

                const match = btnText.match(/(\d+)/);
                if (match) {
                  requiredFiles = parseInt(match[1], 10);
                }

                const label = getIpoFieldLabel(uploadField);
                const isMainArtwork = /^artwork upload$/i.test(label.trim());
                const uploadedUrlProperty = form.querySelector(`input[name="properties[${CSS.escape(label)} URL]"]`);
                const uploadedUrlCount = uploadedUrlProperty?.value
                  ? uploadedUrlProperty.value.split('\n').map((v) => v.trim()).filter(Boolean).length
                  : 0;

                const selectedOptionText = Array.from(
                  document.querySelectorAll('.intaprint-product-options .ipo-field select')
                )
                  .map((select) => {
                    const option = select.options[select.selectedIndex];
                    return `${select.value || ''} ${option?.textContent || ''}`.toLowerCase();
                  })
                  .join(' ');

                if (isMainArtwork && selectedOptionText.includes('different image each side')) {
                  requiredFiles = Math.max(requiredFiles, 2);
                }

                if (isMainArtwork && (selectedOptionText.includes('different image on each side') ||
                    selectedOptionText.includes('different design on each side'))) {
                  requiredFiles = Math.max(requiredFiles, 2);
                }

                if (isMainArtwork && selectedOptionText.includes('2 unique images')) {
                  requiredFiles = Math.max(requiredFiles, 2);
                }

                // One main image plus the separately uploaded QR-code set covers both sides.
                if (/^artwork upload$/i.test(label.trim()) && intaprintQrProvidesSecondSide()) {
                  requiredFiles = 1;
                }

                // IPO clears the native file input after uploading. Its success card
                // displays the retained artwork, even if hidden properties belong
                // to a different Shopify form or have not yet synchronised.
                const uploadCard = uploadField.querySelector('.ipo-upload-card');
                const uploadSucceeded = !!uploadCard &&
                  !uploadCard.hidden &&
                  !uploadCard.querySelector('.ipo-upload-tick--uploading') &&
                  /Artwork Uploaded Successfully/i.test(uploadCard.querySelector('.ipo-upload-title')?.textContent || '');
                const completedPreviewCount = uploadSucceeded
                  ? uploadCard.querySelectorAll('.ipo-upload-preview-item').length
                  : 0;
                const actualUploaded = Math.max(uploadedFiles, uploadedUrlCount, completedPreviewCount);

                if (actualUploaded < requiredFiles) {
                  const err = showInlineIpoError(
                    uploadField,
                    `Please upload ${requiredFiles} ${requiredFiles === 1 ? 'file' : 'files'} for: ${label}`
                  );
                  if (!firstError) firstError = err;
                }
              });

              if (firstError) {
                evt.preventDefault();
                evt.stopPropagation();
                evt.stopImmediatePropagation();

                document.querySelectorAll('fly-to-cart').forEach((el) => el.remove());

                firstError.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });

                return false;
              }
            }

            const selector = productInfo.querySelector('quantity-selector-component');
            if (!selector) {
              submitPrintedFormOnce();
              return;
            }

            const input = selector.querySelector('input[name="quantity"], input[name="updates[]"]');
            if (!input) {
              submitPrintedFormOnce();
              return;
            }

            if (isSampleOrderSelected()) {
              let val = Number(input.value);
              if (!Number.isFinite(val) || val < 1) val = 1;
              setQuantityValue(selector, val);
              submitPrintedFormOnce();
              return;
            }

            const rules = getRules();
            let val = Number(input.value);
            if (!Number.isFinite(val)) val = rules.min;

            val = normaliseQuantityForRules(val, rules);

            setQuantityValue(selector, val, rules);
            submitPrintedFormOnce();
          },
          true
        );
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot, { once: true });
      } else {
        boot();
      }
    },

    /* ----------------------------
       Sticky add-to-cart stuck state (moved from theme.liquid)
    ---------------------------- */
    bindStickyATC() {
      const boot = () => {
        const sticky = document.querySelector('sticky-add-to-cart');
        const bar = sticky?.querySelector('.sticky-add-to-cart__bar');
        const anchor =
          document.querySelector('.product-information [data-buy-buttons]') ||
          document.querySelector('.product-information [ref="productFormButtons"]');

        if (!bar || !anchor) return;

        const io = new IntersectionObserver(
          (entries) => {
            bar.dataset.stuck = entries[0]?.isIntersecting ? 'false' : 'true';
          },
          { threshold: 0.05 }
        );

        io.observe(anchor);
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot, { once: true });
      } else {
        boot();
      }
    },
  };

  Intaprint.init();
})();

/* ================================
   INTAPRINT CHECKOUT-STYLE FORM
================================ */
(function () {
  function getRoot() {
    return document.querySelector('.product-information') || document;
  }

  function isPrinted() {
    const root = getRoot();
    const texts = [];

    root.querySelectorAll('variant-picker input:checked').forEach((el) => {
      const label = root.querySelector(`label[for="${CSS.escape(el.id)}"]`) || el.closest('label');
      if (label) texts.push(label.textContent.trim());
    });

    root.querySelectorAll('variant-picker select').forEach((select) => {
      const opt = select.options[select.selectedIndex];
      if (opt) texts.push(opt.textContent.trim());
    });

    return texts.some((t) => /printed/i.test(t));
  }

  function getWrap() {
    return document.querySelector('[data-intaprint-proof-wrap]');
  }

  function getFormWrap() {
    return document.querySelector('[data-intaprint-proof-form-wrap]');
  }

  function getOpenBtn() {
    return document.querySelector('[data-intaprint-proof-btn]');
  }

  function getSubmitBtn() {
    return document.querySelector('[data-intaprint-proof-submit]');
  }

  function getErrorBox() {
    return document.querySelector('[data-intaprint-proof-error]');
  }

  function toggleProofUI() {
    const wrap = getWrap();
    const formWrap = getFormWrap();
    if (!wrap) return;

    const show = isPrinted();
    wrap.hidden = !show;

    if (!show && formWrap) {
      formWrap.hidden = true;
    }
  }


  function validateProductOptionsForProofOrder() {
    const productForm = document.querySelector('form[action^="/cart/add"]');
    if (!productForm || !isPrinted()) return true;

    const optionFields = Array.from(document.querySelectorAll('.intaprint-product-options .ipo-field'));
    if (!optionFields.length) return true;

    const isFieldVisible = (field) =>
      !!(field.offsetWidth || field.offsetHeight || field.getClientRects().length);

    const getIpoFieldLabel = (field) => {
      const label = field.querySelector('.ipo-label');
      if (!label) return 'this option';

      return Array.from(label.childNodes)
        .filter((node) => !(node.nodeType === 1 && node.classList?.contains('ipo-required')))
        .map((node) => node.textContent || '')
        .join('')
        .replace('*', '')
        .trim() || 'this option';
    };

    const clearInlineIpoErrors = () => {
      document
        .querySelectorAll('.intaprint-ipo-inline-error, .intaprint-upload-required-error')
        .forEach((el) => el.remove());
    };

    const showInlineIpoError = (field, message) => {
      const error = document.createElement('div');
      error.className = 'intaprint-ipo-inline-error';
      error.style.padding = '4px 0';
      error.style.marginTop = '4px';
      error.style.marginBottom = '8px';
      error.style.border = '0';
      error.style.background = 'transparent';
      error.style.color = '#c93a3a';
      error.style.fontSize = '14px';
      error.style.fontWeight = '400';
      error.style.width = 'auto';
      error.innerHTML = `⚠ ${message}`;
      field.appendChild(error);
      return error;
    };

    clearInlineIpoErrors();
    let firstError = null;

    optionFields.forEach((field) => {
      if (!isFieldVisible(field)) return;
      if (!field.querySelector('.ipo-required')) return;
      if (field.querySelector('input[type="file"]')) return;

      const label = getIpoFieldLabel(field);
      const select = field.querySelector('select');
      const swatchLabel = field.querySelector('.ipo-sd-label')?.textContent?.trim() || '';
      const hasSwatchDropdown = !!field.querySelector('.ipo-sd');
      const hasVisibleSwatchValue = hasSwatchDropdown && swatchLabel && swatchLabel.toLowerCase() !== 'select a value';
      const textarea = field.querySelector('textarea');
      const textInput = field.querySelector('input:not([type="file"]):not([type="hidden"]):not([type="radio"]):not([type="checkbox"])');
      const choiceInputs = field.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      const checkedChoice = field.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');

      let message = '';

      if (hasSwatchDropdown) {
        if (!hasVisibleSwatchValue) message = `Please select a value for: ${label}`;
      } else if (select && !String(select.value || '').trim()) {
        message = `Please select a value for: ${label}`;
      } else if (textarea && !String(textarea.value || '').trim()) {
        message = `${label} is required.`;
      } else if (textInput && !String(textInput.value || '').trim()) {
        message = `${label} is required.`;
      } else if (choiceInputs.length && !checkedChoice) {
        message = `Please select a value for: ${label}`;
      }

      if (message) {
        const err = showInlineIpoError(field, message);
        if (!firstError) firstError = err;
      }
    });

    const requiredUploadFields = optionFields.filter((field) => {
      const fileInput = field.querySelector('input[type="file"]');
      const isRequired = !!field.querySelector('.ipo-required');
      return fileInput && isFieldVisible(field) && isRequired;
    });

    requiredUploadFields.forEach((uploadField) => {
      const fileInput = uploadField.querySelector('input[type="file"]');
      const uploadedFiles = fileInput?.files ? fileInput.files.length : 0;
      const uploadBtn = uploadField.querySelector('.ipo-file-btn');
      const btnText = uploadBtn?.textContent?.trim() || '';
      let requiredFiles = 1;

      const match = btnText.match(/(\d+)/);
      if (match) requiredFiles = parseInt(match[1], 10);

      const label = getIpoFieldLabel(uploadField);
                const isMainArtwork = /^artwork upload$/i.test(label.trim());
      const uploadedUrlProperty = productForm.querySelector(`input[name="properties[${CSS.escape(label)} URL]"]`);
      const uploadedUrlCount = uploadedUrlProperty?.value
        ? uploadedUrlProperty.value.split('\n').map((v) => v.trim()).filter(Boolean).length
        : 0;

      const selectedOptionText = Array.from(document.querySelectorAll('.intaprint-product-options .ipo-field select'))
        .map((select) => {
          const option = select.options[select.selectedIndex];
          return `${select.value || ''} ${option?.textContent || ''}`.toLowerCase();
        })
        .join(' ');

      if (isMainArtwork && selectedOptionText.includes('different image each side')) requiredFiles = Math.max(requiredFiles, 2);
      if (isMainArtwork && (selectedOptionText.includes('different image on each side') || selectedOptionText.includes('different design on each side'))) requiredFiles = Math.max(requiredFiles, 2);
      if (isMainArtwork && selectedOptionText.includes('2 unique images')) requiredFiles = Math.max(requiredFiles, 2);
      // One main image plus the separately uploaded QR-code set covers both sides.
      if (/^artwork upload$/i.test(label.trim()) && intaprintQrProvidesSecondSide()) {
        requiredFiles = 1;
      }

      // IPO clears the native file input after uploading. Its success card
                // displays the retained artwork, even if hidden properties belong
                // to a different Shopify form or have not yet synchronised.
                const uploadCard = uploadField.querySelector('.ipo-upload-card');
                const uploadSucceeded = !!uploadCard &&
                  !uploadCard.hidden &&
                  !uploadCard.querySelector('.ipo-upload-tick--uploading') &&
                  /Artwork Uploaded Successfully/i.test(uploadCard.querySelector('.ipo-upload-title')?.textContent || '');
                const completedPreviewCount = uploadSucceeded
                  ? uploadCard.querySelectorAll('.ipo-upload-preview-item').length
                  : 0;
                const actualUploaded = Math.max(uploadedFiles, uploadedUrlCount, completedPreviewCount);

      if (actualUploaded < requiredFiles) {
        const err = showInlineIpoError(
          uploadField,
          `Please upload ${requiredFiles} ${requiredFiles === 1 ? 'file' : 'files'} for: ${label}`
        );
        if (!firstError) firstError = err;
      }
    });

    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    return true;
  }

  // An opened proof-order form must not remain available after required
  // artwork is removed. Check the upload cards without showing validation
  // errors, and preserve all customer-entered form values when hiding it.
  function proofArtworkIsIncomplete() {
    const optionFields = document.querySelectorAll('.intaprint-product-options .ipo-field');
    for (const field of optionFields) {
      if (!field.querySelector('input[type="file"]') || !field.querySelector('.ipo-required')) continue;
      if (!(field.offsetWidth || field.offsetHeight || field.getClientRects().length)) continue;

      const card = field.querySelector('.ipo-upload-card');
      const count = card && !card.hidden && !card.querySelector('.ipo-upload-tick--uploading')
        ? card.querySelectorAll('.ipo-upload-preview-item').length
        : 0;

      const optionText = Array.from(document.querySelectorAll('.intaprint-product-options .ipo-field select'))
        .map(select => `${select.value || ''} ${select.options[select.selectedIndex]?.textContent || ''}`.toLowerCase())
        .join(' ');
      const mainArtwork = /artwork upload/i.test(field.querySelector('.ipo-label')?.textContent || '');
      const needsTwo = mainArtwork && !intaprintQrProvidesSecondSide() && /different (?:image|design)(?: on)? each side|different (?:image|design) on each side|2 unique images/.test(optionText);
      const buttonText = field.querySelector('.ipo-file-btn')?.textContent || '';
      const buttonCount = Number((buttonText.match(/(?:upload|choose)\D*(\d+)/i) || [])[1] || 0);
      const required = mainArtwork && intaprintQrProvidesSecondSide()
        ? 1
        : Math.max(needsTwo ? 2 : 1, buttonCount);
      if (count < required) return true;
    }
    return false;
  }

  function hideStaleProofForm() {
    const formWrap = getFormWrap();
    if (formWrap && !formWrap.hidden && proofArtworkIsIncomplete()) {
      formWrap.hidden = true;
      clearFormError();
    }
  }

  function openForm() {
    if (!validateProductOptionsForProofOrder()) return;

    const formWrap = getFormWrap();
    if (!formWrap) return;
    formWrap.hidden = false;
    clearFormError();
    formWrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function getVal(attr) {
    return document.querySelector(`[data-intaprint-proof-${attr}]`)?.value?.trim() || '';
  }

  function getChecked(attr) {
    return !!document.querySelector(`[data-intaprint-proof-${attr}]`)?.checked;
  }

  // Phone is optional. Convert familiar local UK/Ireland formats to E.164 for Shopify.
  // Never guess a country code for other international numbers.
  function formatPhone(phone, countryCode) {
    const raw = String(phone || '').trim();
    if (!raw) return { value: '', valid: true };

    const clean = raw.replace(/[\s().-]/g, '');
    if (!/^\+?\d+$/.test(clean)) return { value: '', valid: false };

    let formatted = clean.startsWith('00') ? `+${clean.slice(2)}` : clean;
    const country = String(countryCode || 'GB').toUpperCase();

    if (formatted.startsWith('+')) {
      // Explicit international number; leave the customer's country code intact.
    } else if (country === 'IE') {
      if (formatted.startsWith('353')) formatted = `+${formatted}`;
      else if (formatted.startsWith('0')) formatted = `+353${formatted.slice(1)}`;
      else return { value: '', valid: false };
    } else if (['GB', 'JE', 'GG', 'IM'].includes(country)) {
      if (formatted.startsWith('44') && formatted.length >= 11) formatted = `+${formatted}`;
      else if (formatted.startsWith('0')) formatted = `+44${formatted.slice(1)}`;
      else if (formatted.length === 10) formatted = `+44${formatted}`;
      else return { value: '', valid: false };
    } else {
      return { value: '', valid: false };
    }

    // E.164 supports up to 15 digits. UK national numbers (incl. Crown
    // Dependencies) normally have ten digits after +44.
    const digits = formatted.slice(1);
    const valid = /^\d{8,15}$/.test(digits) &&
      !digits.startsWith('0') &&
      (!formatted.startsWith('+44') || /^\+44\d{10}$/.test(formatted));
    return { value: valid ? formatted : '', valid };
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function showFormError(message) {
    const box = getErrorBox();
    if (!box) {
      alert(message);
      return;
    }

    box.textContent = message;
    box.hidden = false;
    box.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function clearFormError() {
    const box = getErrorBox();
    if (!box) return;
    box.textContent = '';
    box.hidden = true;
  }

  function buildShippingAddress() {
    return {
      firstName: getVal('first-name'),
      lastName: getVal('last-name'),
      company: getVal('company'),
      address1: getVal('address1'),
      address2: getVal('address2'),
      city: getVal('city'),
      province: getVal('province'),
      zip: getVal('zip'),
      countryCode: getVal('country')
    };
  }

  function buildBillingAddress() {
    return {
      firstName: getVal('billing-first-name'),
      lastName: getVal('billing-last-name'),
      company: getVal('billing-company'),
      address1: getVal('billing-address1'),
      address2: getVal('billing-address2'),
      city: getVal('billing-city'),
      province: getVal('billing-province'),
      zip: getVal('billing-zip'),
      countryCode: getVal('billing-country')
    };
  }

  function setInvalid(attr, invalid) {
    const el = document.querySelector(`[data-intaprint-proof-${attr}]`);
    if (!el) return;
    if (invalid) {
      el.setAttribute('aria-invalid', 'true');
    } else {
      el.removeAttribute('aria-invalid');
    }
  }

  function toggleBillingFields() {
    const billingFields = document.querySelector('[data-intaprint-proof-billing-fields]');
    if (!billingFields) return;
    billingFields.hidden = getChecked('billing-same');
  }

  function validateForm() {
    let ok = true;

    clearFormError();

    const required = [
      'email',
      'first-name',
      'last-name',
      'address1',
      'city',
      'zip'
    ];

    required.forEach((key) => {
      const empty = !getVal(key);
      setInvalid(key, empty);
      if (empty) ok = false;
    });

    const email = getVal('email');
    if (email && !isValidEmail(email)) {
      setInvalid('email', true);
      showFormError('Please enter a valid email address.');
      return false;
    }

    const checkedPhone = formatPhone(getVal('phone'), getVal('country'));
    setInvalid('phone', !checkedPhone.valid);
    if (!checkedPhone.valid) {
      showFormError('Please enter a valid telephone number, including the area or country code.');
      document.querySelector('[data-intaprint-proof-phone]')?.focus();
      return false;
    }

    if (!getChecked('billing-same')) {
      const billingRequired = [
        'billing-first-name',
        'billing-last-name',
        'billing-address1',
        'billing-city',
        'billing-zip'
      ];

      billingRequired.forEach((key) => {
        const empty = !getVal(key);
        setInvalid(key, empty);
        if (empty) ok = false;
      });
    }

    if (!ok) {
      showFormError('Please complete all required fields.');
    }

    return ok;
  }



  function formatExpressProductionDate(value) {
    const raw = String(value || '').trim();
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!match) return raw;

    const date = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );

    if (Number.isNaN(date.getTime())) return raw;

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  function prepareExpressOrderDetails(productProperties, pricing) {
    const properties = { ...(productProperties || {}) };
    const paidOptions = Array.isArray(pricing?.paidOptions)
      ? pricing.paidOptions
      : [];

    const isExpress = paidOptions.some(
      (option) =>
        String(option?.title || "").trim().toLowerCase() ===
        "express production"
    );

    return { properties, isExpress };
  }


  function getPayAfterProofPricing(quantity) {
    if (typeof window.IntaprintGetCurrentPricing !== 'function') return null;

    const pricing = window.IntaprintGetCurrentPricing(quantity);

    if (
      !pricing ||
      !Number.isFinite(Number(pricing.productTotalIncl)) ||
      !Number.isFinite(Number(pricing.catalogueUnitPriceIncl))
    ) {
      return null;
    }

    return pricing;
  }

  async function submitPrintedOrder() {
    if (!validateProductOptionsForProofOrder()) return;
    if (!validateForm()) return;

    const variantId = document.querySelector('form[action^="/cart/add"] input[name="id"]')?.value;
    const quantity =
      document.querySelector('.product-information input[name="quantity"]')?.value ||
      document.querySelector('.product-information input[name="updates[]"]')?.value ||
      1;

    if (!variantId) {
      showFormError('Please select a variant.');
      return;
    }

    const sameBilling = getChecked('billing-same');
    const shippingAddress = buildShippingAddress();
    const billingAddress = sameBilling ? { ...shippingAddress } : buildBillingAddress();

    const productForm = document.querySelector('form[action^="/cart/add"]');
    const artworkFileInput = productForm?.querySelector('input[type="file"]');

    function collectProductProperties(form) {
      const props = {};
      if (!form) return props;

      const fields = form.querySelectorAll('[name^="properties["]');

      fields.forEach((field) => {
        const name = field.getAttribute('name') || '';
        const match = name.match(/^properties\[(.*)\]$/);
        if (!match) return;

        const key = match[1];
        if (!key) return;

        if (field instanceof HTMLInputElement) {
          const type = (field.type || '').toLowerCase();

          if (type === 'file') {
            if (field.files && field.files.length) {
              props[key] = field.files[0].name;
            }
            return;
          }

          if (type === 'checkbox') {
            if (field.checked) {
              props[key] = field.value || 'Yes';
            }
            return;
          }

          if (type === 'radio') {
            if (field.checked) {
              props[key] = field.value || '';
            }
            return;
          }

          const value = (field.value || '').trim();
          if (value) {
            props[key] = value;
          }
          return;
        }

        if (field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) {
          const value = (field.value || '').trim();
          if (value) {
            props[key] = value;
          }
        }
      });

      return props;
    }

    const productProperties = collectProductProperties(productForm);

    // Keep a friendly filename too, even if the uploader stores URL separately
    if (artworkFileInput?.files?.[0]?.name) {
      productProperties['Artwork Filename'] = artworkFileInput.files[0].name;
    }

    const pricing = getPayAfterProofPricing(quantity);

    if (!pricing) {
      showFormError('The product price could not be calculated. Please refresh the page and try again.');
      return;
    }

    const expressDetails = prepareExpressOrderDetails(
      productProperties,
      pricing
    );

    const orderTags = ['PROOF REQUESTED', 'PRINTED'];

    if (expressDetails.isExpress) {
      orderTags.push('EXPRESS');
    }

    const payload = {
      email: getVal('email'),
      phone: formatPhone(getVal('phone'), getVal('country')).value,
      tags: orderTags,
      shippingAddress,
      billingAddress,
      lineItems: [
        {
          variantId: `gid://shopify/ProductVariant/${variantId}`,
          quantity: Number(quantity),
          customAttributes: expressDetails.properties,
          catalogueUnitPriceIncl: pricing.catalogueUnitPriceIncl,
          catalogueTotalIncl: pricing.catalogueTotalIncl,
          productUnitPriceIncl: pricing.productUnitPriceIncl,
          productTotalIncl: pricing.productTotalIncl,
          volumeDiscountTotalIncl: pricing.volumeDiscountTotalIncl,
          paidOptions: pricing.paidOptions
        }
      ]
    };

    const submitBtn = getSubmitBtn();
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting…';
    }

    try {
      clearFormError();

      const res = await fetch('/apps/intaprint-options/draft-order/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const raw = await res.text();
      console.log('Draft order response status:', res.status);

      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch (e) {
        throw new Error('We could not submit your order just now. Please try again shortly. If the problem continues, contact us and we will help.');
      }

      if (json.success) {
        clearFormError();
        window.location.href = '/pages/printed-order-submitted';
      } else {
        console.error(json);
        const errorText = String(json.error || '');
        if (/phone is invalid|invalid phone/i.test(errorText)) {
          setInvalid('phone', true);
          showFormError('Please check your telephone number and try again.');
          document.querySelector('[data-intaprint-proof-phone]')?.focus();
        } else {
          showFormError('There was a problem submitting your printed order. Please check your details and try again. If the problem continues, contact us.');
        }
      }
    } catch (err) {
      console.error(err);
      showFormError('We could not submit your order just now. Please try again shortly. If the problem continues, contact us and we will help.');
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit Printed Order';
      }
    }
  }

  function initProofForm() {
    document.addEventListener('variant:changed', () => {
      toggleProofUI();
      toggleBillingFields();
    });

    document.addEventListener('change', (e) => {
      if (e.target.closest('variant-picker')) {
        setTimeout(toggleProofUI, 50);
      }

      if (e.target.matches('[data-intaprint-proof-billing-same]')) {
        toggleBillingFields();
      }
    });

    const openBtn = getOpenBtn();
    if (openBtn && !openBtn.dataset.intaprintBound) {
      openBtn.dataset.intaprintBound = 'true';
      openBtn.addEventListener('click', openForm);
    }

    const submitBtn = getSubmitBtn();
    if (submitBtn && !submitBtn.dataset.intaprintBound) {
      submitBtn.dataset.intaprintBound = 'true';
      submitBtn.addEventListener('click', submitPrintedOrder);
    }

    // IPO rerenders upload fields after removal/replacement. Observe only the
    // options container; do not reset its upload state or the proof-form inputs.
    const optionsRoot = document.querySelector('.intaprint-product-options');
    if (optionsRoot) {
      const uploadObserver = new MutationObserver(() => hideStaleProofForm());
      uploadObserver.observe(optionsRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'style'] });
    }

    toggleProofUI();
    toggleBillingFields();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProofForm, { once: true });
  } else {
    initProofForm();
  }
})();